/**
 * Research jobs API (WP25 / Spec §K).
 */

import {
  createResearchJobInputSchema,
  createResearchJobResultSchema,
  researchJobSchema,
  type CreateResearchJobInput,
  type CreateResearchJobResult,
  type ResearchJob,
} from "@carago/shared";
import { z } from "zod";
import { apiFetch, type ApiFetchOptions } from "./api.js";

export const researchJobListSchema = z.object({
  items: z.array(researchJobSchema),
  next_cursor: z.string().nullable(),
});

export type ResearchJobList = z.infer<typeof researchJobListSchema>;

export type ListResearchJobsParams = {
  cursor?: string | null;
  limit?: number;
};

function withQuery(
  path: string,
  params: Record<string, string | number | undefined | null>,
): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    qs.set(key, String(value));
  }
  const query = qs.toString();
  return query ? `${path}?${query}` : path;
}

export async function listResearchJobs(
  params: ListResearchJobsParams = {},
  options?: ApiFetchOptions,
): Promise<ResearchJobList> {
  const raw = await apiFetch<unknown>(
    withQuery("/v1/research-jobs", {
      cursor: params.cursor,
      limit: params.limit,
    }),
    { method: "GET", ...options },
  );
  return researchJobListSchema.parse(raw);
}

export async function getResearchJob(
  id: string,
  options?: ApiFetchOptions,
): Promise<ResearchJob> {
  const raw = await apiFetch<unknown>(`/v1/research-jobs/${id}`, {
    method: "GET",
    ...options,
  });
  return researchJobSchema.parse(raw);
}

export async function createResearchJob(
  input: CreateResearchJobInput,
  options?: ApiFetchOptions,
): Promise<CreateResearchJobResult> {
  const body = createResearchJobInputSchema.parse(input);
  const raw = await apiFetch<unknown>("/v1/research-jobs", {
    method: "POST",
    body,
    ...options,
  });
  return createResearchJobResultSchema.parse(raw);
}

export async function cancelResearchJob(
  id: string,
  options?: ApiFetchOptions,
): Promise<ResearchJob> {
  const raw = await apiFetch<unknown>(`/v1/research-jobs/${id}/cancel`, {
    method: "POST",
    ...options,
  });
  return researchJobSchema.parse(raw);
}

export async function resumeResearchJob(
  id: string,
  options?: ApiFetchOptions,
): Promise<ResearchJob> {
  const raw = await apiFetch<unknown>(`/v1/research-jobs/${id}/resume`, {
    method: "POST",
    ...options,
  });
  return researchJobSchema.parse(raw);
}

export function canCancelResearchJob(status: ResearchJob["status"]): boolean {
  return (
    status !== "completed" &&
    status !== "cancelled" &&
    status !== "cancelling"
  );
}

export function canResumeResearchJob(status: ResearchJob["status"]): boolean {
  return status === "failed" || status === "cancelled";
}

export function formatCostUsd(value: number): string {
  return new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatJobDate(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("de-CH", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}
